import { AdminBillingRoutes } from '@/admin/billing/routes';
import PlanFeaturesList from '@/plan-features/plan-features-list';
import { AdminCoursesRoutes } from '@/admin/courses/routes';
import { AdminEventsRoutes } from '@/admin/events/routes';
import { AdminGroupsRoutes } from '@/admin/groups/routes';
import { AdminPlaylistsRoutes } from '@/admin/playlists/routes';
import { AdminTopicsRoutes } from '@/admin/topics/routes';
import { AdminUsersRoutes } from '@/admin/users/routes';
import { Names, Paths } from '@/reporting/routes';
import { NavigationSidebarItem } from '@/common/components/navigation/sidebar/model';
import { useLaunchDarklyFeatureStore } from '@/launch-darkly/store';
import { AdminSkillsRoutes } from '@/admin/skills/routes';
import { AdminSettingsRoutes } from '@/admin/settings/routes';
import { AdminApiRoutes } from '@/admin/api-tokens/routes';

import { Permissions } from '@/permissions';
import { AuthenticationServiceTokens as Tokens } from '@/authentication/di-tokens';
import { useInjector } from '@/di/use-injector';
import { computed } from 'vue';
import { useRoute, useTranslation } from '@/common/composables';
import { usePermissions } from '@/permissions/composables/use-permissions';
import { useUserProfileDetailsStore } from '@/common/store/user-details/store';

import '@eloomi/icons/products-pages/products-and-pages-topic';
import '@eloomi/icons/products-pages/products-and-pages-billing';
import '@eloomi/icons/products-pages/products-and-pages-course';
import '@eloomi/icons/products-pages/products-and-pages-segments';
import '@eloomi/icons/actions/actions-user';
import '@eloomi/icons/actions/actions-users';

export function useSidebarNavigation() {
    const route = useRoute();
    const injector = useInjector();
    const platformService = injector.resolve(Tokens.PlatformResolutionService);
    const { trans } = useTranslation();
    const { hasPermission, hasAnyPermission, canAccessGroupReport } = usePermissions();
    const launchDarklyFeatureStore = useLaunchDarklyFeatureStore();
    const userProfileDetailsStore = useUserProfileDetailsStore();

    const launchDarklyFeaturesList = launchDarklyFeatureStore.state.launchDarklyFeaturesList;

    const reportingContent = computed<NavigationSidebarItem[]>(() => {
        return [
            {
                title: trans('spa.navigation.sections.reports.my-learning'),
                icon: 'elm-actions-user-icon',
                location: hasPermission(Permissions.Reporting) ? Names.MY_ADMIN_REPORT : Names.MY_REPORT,
                order: 1,
                access: true,
            },
            {
                title: trans('spa.navigation.sections.reports.courses'),
                icon: 'elm-products-and-pages-course-icon',
                order: 2,
                location: Names.COURSES_REPORT,
                featureName: PlanFeaturesList.COURSES,
                access: hasPermission(Permissions.Reporting),
            },
            {
                title: trans('spa.navigation.sections.reports.users'),
                icon: 'elm-actions-users-icon',
                order: 3,
                location: Names.USERS_REPORT,
                access: hasPermission(Permissions.Reporting),
            },
            {
                title: trans('spa.navigation.sections.reports.groups'),
                pageTitle: trans('spa.navigation.sections.reports.groups-report'),
                location: hasPermission(Permissions.Reporting) ? Names.GROUPS_REPORT : Names.MY_GROUP_REPORT,
                icon: 'elm-statistics-and-hierarchy-organizational-structure-icon',
                featureName: [PlanFeaturesList.USER_PERMISSIONS, PlanFeaturesList.GROUPS],
                order: 4,
                access: canAccessGroupReport.value,
                launchDarklyFeatureName: launchDarklyFeaturesList.GROUPS_REPORTING_MANAGER,
            },
            {
                title: trans('spa.navigation.sections.reports.segments'),
                icon: 'elm-products-and-pages-segments-icon',
                order: 5,
                location: Names.SEGMENT_OVERVIEW,
                featureName: PlanFeaturesList.SEGMENT_REPORTING,
                access: hasPermission(Permissions.Reporting),
            },
            {
                title: trans('spa.navigation.sections.reports.skills'),
                icon: 'elm-products-and-pages-skills-regular-icon',
                order: 6,
                location: Names.SKILLS_REPORT,
                featureName: PlanFeaturesList.SKILLS,
                access: hasPermission(Permissions.Reporting),
            },
        ];
    });

    const adminSettingsItem = computed<NavigationSidebarItem>(() => {
        return {
            name: 'settings',
            title: trans('spa.navigation.sections.admin.settings'),
            icon: 'elm-products-and-pages-settings-icon',
            order: 6,
            access: userProfileDetailsStore.getters.isAuthUserAdmin,
            children: [
                {
                    name: 'branding',
                    title: trans('spa.navigation.sections.admin.branding'),
                    order: 1,
                    location: AdminSettingsRoutes.BRANDING,
                    featureName: PlanFeaturesList.BRANDING,
                },
                {
                    name: 'custom-fields',
                    title: trans('spa.navigation.sections.admin.custom-user-fields'),
                    order: 3,
                    location: AdminSettingsRoutes.CUSTOM_FIELDS,
                    featureName: PlanFeaturesList.CUSTOM_FIELDS,
                },
                {
                    name: 'sso',
                    title: trans('spa.navigation.sections.admin.sso'),
                    order: 4,
                    location: AdminSettingsRoutes.SSO,
                    access: !platformService.isSsoLoginEnabled(),
                },
            ],
        };
    });

    const administrationContent = computed<NavigationSidebarItem[]>(() => {
        return [
            {
                name: 'organization',
                title: trans('spa.navigation.sections.admin.organization'),
                icon: 'elm-actions-people-performance-icon',
                order: 1,
                access: hasAnyPermission(Permissions.AdminGroups, Permissions.AdminUsers),
                children: [
                    {
                        title: trans('spa.navigation.sections.admin.users'),
                        order: 1,
                        location: AdminUsersRoutes.USERS,
                        fallbackIcon: 'users',
                        access: hasPermission(Permissions.AdminUsers),
                    },
                    {
                        title: trans('spa.navigation.sections.admin.groups'),
                        order: 2,
                        location: AdminGroupsRoutes.GROUPS,
                        featureName: PlanFeaturesList.GROUPS,
                        access: hasPermission(Permissions.AdminGroups),
                    },
                ],
            },
            {
                name: 'learning',
                title: trans('spa.navigation.sections.admin.learning'),
                icon: 'elm-products-and-pages-course-builder-chapter-icon',
                order: 2,
                access: hasAnyPermission(Permissions.AdminCourses, Permissions.AdminEvents, Permissions.AdminPlaylists),
                children: [
                    {
                        name: 'courses',
                        title: trans('spa.navigation.sections.admin.courses'),
                        order: 1,
                        location: AdminCoursesRoutes.COURSES_ADMINISTRATION,
                        featureName: PlanFeaturesList.COURSES,
                        access: hasPermission(Permissions.AdminCourses),
                    },
                    {
                        name: 'events',
                        title: trans('spa.navigation.sections.admin.events'),
                        order: 2,
                        location: AdminEventsRoutes.EVENTS_OVERVIEW,
                        launchDarklyFeatureName: launchDarklyFeaturesList.EVENTS_MANAGEMENT,
                        featureName: PlanFeaturesList.EVENTS,
                        access: hasPermission(Permissions.AdminEvents),
                    },
                    {
                        name: 'playlists',
                        title: trans('spa.navigation.sections.admin.playlists'),
                        order: 3,
                        location: AdminPlaylistsRoutes.PLAYLISTS_OVERVIEW,
                        featureName: PlanFeaturesList.PLAYLISTS,
                        access: hasPermission(Permissions.AdminPlaylists),
                    },
                ],
            },
            {
                name: 'skills',
                title: trans('spa.navigation.sections.admin.skills'),
                icon: 'elm-products-and-pages-skills-regular-icon',
                order: 3,
                access: hasPermission(Permissions.AdminSkills),
                children: [
                    {
                        name: 'business-titles',
                        title: trans('spa.navigation.sections.admin.business-titles'),
                        order: 1,
                        location: AdminSkillsRoutes.BUSINESS_TITLES,
                        featureName: PlanFeaturesList.SKILLS,
                    },
                    {
                        name: 'skills-library',
                        title: trans('spa.navigation.sections.admin.skills-library'),
                        order: 2,
                        location: AdminSkillsRoutes.SKILLS_LIBRARY,
                        featureName: PlanFeaturesList.SKILLS,
                    },
                ],
            },
            {
                title: trans('spa.navigation.sections.admin.categories-and-topics'),
                icon: 'elm-products-and-pages-topic-icon',
                order: 4,
                location: AdminTopicsRoutes.TOPICS,
                featureName: PlanFeaturesList.CATEGORIES_TOPICS,
                access: hasPermission(Permissions.AdminCategoriesTopics),
            },
            {
                title: trans('spa.navigation.sections.admin.billing'),
                icon: 'elm-products-and-pages-billing-icon',
                order: 5,
                location: AdminBillingRoutes.BILLING,
                featureName: PlanFeaturesList.BILLING,
                featureHideContent: true,
                access: hasPermission(Permissions.AdminBilling),
            },
            {
                title: trans('spa.navigation.sections.admin.integrations'),
                icon: 'elm-statistics-and-hierarchy-integration-icon',
                order: 6,
                location: AdminApiRoutes.Integrations,
                featureName: PlanFeaturesList.API,
                featureHideContent: true,
                access: hasPermission(Permissions.AdminApi),
            },
            adminSettingsItem.value,
        ];
    });

    const pageType = computed<'report' | 'administration'>(() => {
        return route.path.includes(Paths.REPORT) ? 'report' : 'administration';
    });

    const content = computed<NavigationSidebarItem[]>(() => {
        return pageType.value === 'report' ? reportingContent.value : administrationContent.value;
    });

    return { content, administrationContent, adminSettingsItem };
}
